import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Input, Button, Tag, Select, Popconfirm, Space, message } from 'antd';
import { PlusOutlined, ImportOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  getPartnerItems,
  createPartnerItem,
  updatePartnerItem,
  removePartnerItem,
  bulkCreatePartnerItems,
} from '../../modules/partners/api';
import dayjs from 'dayjs';
import './PartnerItemsModal.scss';

const STATUS_COLORS = {
  AVAILABLE: 'green',
  IN_CIRCULATION: 'blue',
  LOST: 'red',
  DAMAGED: 'orange',
};

const STATUS_OPTIONS = Object.keys(STATUS_COLORS).map((s) => ({
  label: s.replace('_', ' '),
  value: s,
}));

const PAGE_SIZE = 10;

export default function PartnerItemsModal({ open, onClose, itemType }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, current: 1 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [newSerial, setNewSerial] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchItems = useCallback(
    async (page = 1, q = search) => {
      if (!itemType?.id) return;
      setLoading(true);
      try {
        const { response, error } = await getPartnerItems(itemType.id, {
          page,
          take: PAGE_SIZE,
          q: q || undefined,
        });
        if (error) {
          message.error(error.message || 'Failed to load items');
          return;
        }
        setItems(response?.items || []);
        setPagination({
          total: response?.pagination?.totalItems || 0,
          current: response?.pagination?.currentPage || page,
        });
      } catch {
        message.error('Failed to load items');
      } finally {
        setLoading(false);
      }
    },
    [itemType?.id, search],
  );

  useEffect(() => {
    if (open && itemType?.id) {
      setSearch('');
      fetchItems(1, '');
    }
  }, [open, itemType?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (value) => {
    setSearch(value);
    fetchItems(1, value);
  };

  const handlePageChange = (page) => {
    fetchItems(page);
  };

  const handleAdd = async () => {
    const serial = newSerial.trim();
    if (!serial) return;
    setAddLoading(true);
    try {
      const { error } = await createPartnerItem(itemType.id, { serialNumber: serial });
      if (error) {
        message.error(error.message || 'Failed to add item');
      } else {
        message.success('Item added');
        setNewSerial('');
        fetchItems(1, search);
      }
    } catch {
      message.error('Failed to add item');
    } finally {
      setAddLoading(false);
    }
  };

  const handleBulkImport = async () => {
    const serials = bulkText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!serials.length) return;
    setBulkLoading(true);
    try {
      const { response, error } = await bulkCreatePartnerItems(itemType.id, serials);
      if (error) {
        message.error(error.message || 'Bulk import failed');
      } else {
        const parts = [];
        if (response?.created) parts.push(`${response.created} created`);
        if (response?.skipped) parts.push(`${response.skipped} skipped`);
        message.success(parts.join(', ') || 'Import complete');
        if (response?.skippedSerials?.length) {
          message.warning(`Skipped: ${response.skippedSerials.join(', ')}`);
        }
        setBulkText('');
        fetchItems(1, search);
      }
    } catch {
      message.error('Bulk import failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleStatusChange = async (record, newStatus) => {
    try {
      const { error } = await updatePartnerItem({ id: record.id, status: newStatus });
      if (error) {
        message.error(error.message || 'Failed to update status');
      } else {
        message.success('Status updated');
        fetchItems(pagination.current, search);
      }
    } catch {
      message.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await removePartnerItem(id);
      if (error) {
        message.error(error.message || 'Failed to delete item');
      } else {
        message.success('Item deleted');
        fetchItems(pagination.current, search);
      }
    } catch {
      message.error('Failed to delete item');
    }
  };

  const columns = [
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{(status || '').replace('_', ' ')}</Tag>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
    {
      title: 'Last Seen',
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      width: 140,
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY HH:mm') : '\u2014'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Select
            size="small"
            value={record.status}
            options={STATUS_OPTIONS}
            onChange={(val) => handleStatusChange(record, val)}
            style={{ width: 130 }}
          />
          <Popconfirm title="Delete this item?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={`Items \u2014 ${itemType?.name || ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
      className="partner-items-modal"
    >
      <div className="partner-items-modal__search">
        <Input.Search
          placeholder="Search by serial number..."
          allowClear
          onSearch={handleSearch}
          style={{ width: 300 }}
        />
      </div>

      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          current: pagination.current,
          total: pagination.total,
          pageSize: PAGE_SIZE,
          onChange: handlePageChange,
          showSizeChanger: false,
          showTotal: (total) => `${total} items`,
        }}
      />

      <div className="partner-items-modal__add-section">
        <h4>Add Item</h4>
        <Space>
          <Input
            placeholder="Serial number"
            value={newSerial}
            onChange={(e) => setNewSerial(e.target.value)}
            onPressEnter={handleAdd}
            style={{ width: 250 }}
          />
          <Button type="primary" icon={<PlusOutlined />} loading={addLoading} onClick={handleAdd}>
            Add
          </Button>
        </Space>
      </div>

      <div className="partner-items-modal__bulk-section">
        <h4>Bulk Add</h4>
        <Input.TextArea
          placeholder="Paste serial numbers (one per line)"
          rows={4}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
        />
        <Button
          type="default"
          icon={<ImportOutlined />}
          loading={bulkLoading}
          onClick={handleBulkImport}
          style={{ marginTop: 8 }}
        >
          Import
        </Button>
      </div>
    </Modal>
  );
}
